import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Sun, Shield, Bed, Bath, Square, ShoppingBag, Mountain, GraduationCap, TreePine, TrendingUp, Home, Heart, Car, Plane, Utensils, Building2 } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
import { SEOHead } from '../components/SEOHead';
import { RelatedCommunities } from '../components/RelatedCommunities';
const placeholderListings = [{
  id: 1,
  price: '$385,000',
  address: '3421 Aliante Pkwy',
  area: 'Aliante',
  beds: 3,
  baths: 2,
  sqft: '1,750',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 2,
  price: '$425,000',
  address: '6712 Tee Box Ave',
  area: 'North Las Vegas',
  beds: 4,
  baths: 2.5,
  sqft: '2,100',
  image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
}, {
  id: 3,
  price: '$450,000',
  address: '1234 Desert Oasis Way',
  area: 'Eldorado',
  beds: 4,
  baths: 3,
  sqft: '2,450',
  image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2940&auto=format&fit=crop'
}];
export function NorthLasVegasPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-1223">
      <SEOHead title="North Las Vegas Real Estate & Homes for Sale | Jenny Gong" description="Explore real estate in North Las Vegas, an emerging market with new master-planned communities, excellent investment opportunities, and modern amenities." keywords="North Las Vegas real estate, North Las Vegas homes for sale, Aliante real estate, Valley Vista homes for sale" canonicalUrl="https://jennygong.com/north-las-vegas" ogImage="https://images.unsplash.com/photo-1512915922686-57c11dde9b6b?q=80&w=2946&auto=format&fit=crop" data-id="element-1224" />
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-1225">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1512915922686-57c11dde9b6b?q=80&w=2946&auto=format&fit=crop")'
      }} data-id="element-1226">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-1227"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-1228"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-1229">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-1230">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-1231">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-1232">
              North Las Vegas Homes for Sale
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-1233">
              An emerging market with brand-new communities, exceptional value,
              and tremendous investment potential.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 bg-white" data-id="element-1234">
        <div className="max-w-7xl mx-auto" data-id="element-1235">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1236">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1237">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1238">
                The Valley's{' '}
                <span className="italic text-gold" data-id="element-1239">Best-Kept Secret</span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1240">
                <p data-id="element-1241">
                  North Las Vegas has undergone a remarkable transformation over
                  the past decade, evolving from a quiet suburb into one of the
                  fastest-growing cities in Nevada. With a population exceeding
                  270,000, it offers some of the best real estate value in the
                  entire Las Vegas Valley.
                </p>
                <p data-id="element-1242">
                  The city is home to brand-new master-planned communities like
                  Aliante, Elkhorn Springs, and the rapidly developing areas
                  near the Las Vegas Motor Speedway. Buyers can find beautiful
                  new-construction homes starting in the mid-$300Ks —
                  significantly below comparable homes in{' '}
                  <Link to="/summerlin" className="text-gold hover:underline" data-id="element-1243">
                    Summerlin
                  </Link>{' '}
                  or{' '}
                  <Link to="/henderson" className="text-gold hover:underline" data-id="element-1244">
                    Henderson
                  </Link>
                  .
                </p>
                <p data-id="element-1245">
                  North Las Vegas is also a major economic hub, home to Nellis
                  Air Force Base, the VA Medical Center, Amazon distribution
                  centers, and a growing industrial corridor that's attracting
                  major employers and driving property appreciation.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-1246">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1247">
                <TrendingUp className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1248" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1249">Top</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1250">
                  Appreciation
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1251">
                <Home className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1252" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1253">New</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1254">
                  Construction
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1255">
                <Building2 className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1256" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1257">
                  Major
                </h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1258">
                  Job Growth
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1259">
                <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1260" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1261">
                  Nellis
                </h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1262">
                  Air Force Base
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Communities */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1263">
        <div className="max-w-7xl mx-auto" data-id="element-1264">
          <div className="text-center mb-16" data-id="element-1265">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1266">
              New Development
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1267">
              Master-Planned Communities
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1268">
            {[{
            name: 'Aliante',
            price: '$350K - $550K',
            desc: 'A stunning master-planned community with an 18-hole golf course, nature discovery park, casino resort, and miles of walking trails.',
            img: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
          }, {
            name: 'Elkhorn Springs',
            price: '$380K - $600K',
            desc: 'A newer community offering modern floor plans, community parks, and excellent access to the 215 Beltway and I-15.',
            img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
          }, {
            name: 'Tule Springs',
            price: '$400K - $650K',
            desc: 'Adjacent to the Tule Springs Fossil Beds National Monument, offering unique desert living with modern amenities.',
            img: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
          }].map((n, idx) => <motion.div key={n.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white p-6 shadow-sm border border-warmGray-dark" data-id="element-1269">
                <img src={n.img} alt={n.name} className="w-full h-48 object-cover mb-4" data-id="element-1270" />
                <div className="flex justify-between items-start mb-3" data-id="element-1271">
                  <h3 className="text-xl font-serif text-charcoal" data-id="element-1272">{n.name}</h3>
                  <span className="text-xs font-medium text-gold bg-gold/10 px-2 py-1 rounded whitespace-nowrap" data-id="element-1273">
                    {n.price}
                  </span>
                </div>
                <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1274">
                  {n.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Investment & Economy */}
      <section className="py-24 px-6 bg-white" data-id="element-1275">
        <div className="max-w-7xl mx-auto" data-id="element-1276">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1277">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-8" data-id="element-1278">
              <div data-id="element-1279">
                <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1280">
                  Smart Investment
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1281">
                  Why Investors Love NLV
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1282">
                <div className="bg-warmGray p-6 border border-warmGray-dark" data-id="element-1283">
                  <TrendingUp className="w-6 h-6 text-gold mb-3" data-id="element-1284" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1285">
                    Strong Appreciation
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1286">
                    Property values have seen some of the highest appreciation
                    rates in the valley over the past 5 years.
                  </p>
                </div>
                <div className="bg-warmGray p-6 border border-warmGray-dark" data-id="element-1287">
                  <Home className="w-6 h-6 text-gold mb-3" data-id="element-1288" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1289">
                    Rental Demand
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1290">
                    High demand for rentals near Nellis AFB and the growing
                    industrial corridor creates excellent cash flow
                    opportunities.
                  </p>
                </div>
                <div className="bg-warmGray p-6 border border-warmGray-dark" data-id="element-1291">
                  <Building2 className="w-6 h-6 text-gold mb-3" data-id="element-1292" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1293">
                    Job Growth
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1294">
                    Amazon, UNLV Tech Park, and major logistics companies are
                    driving unprecedented employment growth.
                  </p>
                </div>
                <div className="bg-warmGray p-6 border border-warmGray-dark" data-id="element-1295">
                  <MapPin className="w-6 h-6 text-gold mb-3" data-id="element-1296" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1297">
                    New Infrastructure
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1298">
                    Billions in new roads, schools, and commercial development
                    are transforming the area rapidly.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1299">
              <div className="relative h-[600px]" data-id="element-1300">
                <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2873&auto=format&fit=crop" alt="Investment" className="w-full h-full object-cover" data-id="element-1301" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-1302"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Things To Do */}
      <section className="py-24 bg-warmGray px-6 border-t border-warmGray-dark" data-id="element-1303">
        <div className="max-w-7xl mx-auto" data-id="element-1304">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1305">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1306">
              Entertainment
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1307">Things To Do</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1308">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-1309">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1310">
                <img src="https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?q=80&w=2905&auto=format&fit=crop" alt="Speedway" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1311" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1312">
                Las Vegas Motor Speedway
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1313">
                Home to NASCAR races, drag racing, and driving experiences. One
                of the premier motorsports venues in the country.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-1314">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1315">
                <img src="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=2940&auto=format&fit=crop" alt="Golf" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1316" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1317">
                Aliante Golf Club
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1318">
                A beautifully maintained 18-hole championship course with
                stunning desert and mountain views, open to the public.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-1319">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1320">
                <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=2940&auto=format&fit=crop" alt="Casino" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1321" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1322">
                Aliante Casino + Hotel
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1323">
                A full-service resort with dining, entertainment, a movie
                theater, and a relaxing pool area — all within the community.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="py-24 px-6 bg-white" data-id="element-1324">
        <div className="max-w-7xl mx-auto" data-id="element-1325">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1326">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1327" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1328">
              Education
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1329">
              Top-Rated Schools
            </h2>
            <p className="text-charcoal-light font-light max-w-3xl mx-auto text-lg leading-relaxed" data-id="element-1330">
              North Las Vegas offers a mix of established public schools and
              highly sought-after charter academies, providing excellent
              educational choices for growing families.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1331">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-warmGray p-8 border border-warmGray-dark" data-id="element-1332">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1333">
                Elementary & Middle
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1334">
                <li data-id="element-1335">• Theron H. Goynes Elementary</li>
                <li data-id="element-1336">• Kitty McDonough Ward Elementary</li>
                <li data-id="element-1337">• Eva G. Simmons Elementary</li>
                <li data-id="element-1338">• Brian & Teri Cram Middle School</li>
                <li data-id="element-1339">• Anthony Saville Middle School</li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-warmGray p-8 border border-warmGray-dark" data-id="element-1340">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1341">
                High Schools
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1342">
                <li data-id="element-1343">• Shadow Ridge High School</li>
                <li data-id="element-1344">• Legacy High School</li>
                <li data-id="element-1345">• Centennial High School</li>
                <li data-id="element-1346">• Mojave High School</li>
                <li className="pt-4 italic text-xs text-gold" data-id="element-1347">
                  Shadow Ridge is highly regarded for its strong academic and
                  athletic programs.
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-warmGray p-8 border border-warmGray-dark" data-id="element-1348">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1349">
                Private & Charter
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1350">
                <li data-id="element-1351">• Somerset Academy (Aliante & Losee)</li>
                <li data-id="element-1352">• Legacy Traditional School</li>
                <li data-id="element-1353">• Coral Academy of Science</li>
                <li data-id="element-1354">• Founders Academy</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Parks & Recreation */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1355">
        <div className="max-w-7xl mx-auto" data-id="element-1356">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1357">
            <TreePine className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1358" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1359">
              Outdoor Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1360">
              Parks & Recreation
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1361">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-1362">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1363">
                <img src="https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=2940&auto=format&fit=crop" alt="Craig Ranch Regional Park" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1364" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1365">
                Craig Ranch Regional Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1366">
                A massive 170-acre park featuring a 65,000 sq ft skate park,
                four civic plazas, three dog parks, community gardens, and an
                incredible outdoor amphitheater.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-1367">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1368">
                <img src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=2940&auto=format&fit=crop" alt="Aliante Nature Discovery Park lake and nature trails in North Las Vegas" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1369" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1370">
                Aliante Nature Discovery Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1371">
                Known locally as "Dinosaur Park," this 20-acre facility features
                a massive man-made lake, waterfalls, dinosaur-themed play areas,
                and tennis courts.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Healthcare */}
      <section className="py-24 px-6 bg-white" data-id="element-1372">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1373">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1374">
            <Heart className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1375" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1376">
              Wellness
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1377">
              World-Class Healthcare
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-8" data-id="element-1378">
              North Las Vegas is served by the state-of-the-art{' '}
              <strong data-id="element-1379">VA Medical Center</strong>, providing comprehensive care
              to the region's large veteran population, as well as the nearby{' '}
              <strong data-id="element-1380">Centennial Hills Hospital Medical Center</strong>.
            </p>
            <p className="text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1381">
              The rapidly growing area has also seen a surge in new medical
              plazas, urgent care facilities, and specialized clinics, ensuring
              residents have immediate access to quality care.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Transportation */}
      <section className="py-24 bg-charcoal px-6 text-white" data-id="element-1382">
        <div className="max-w-7xl mx-auto" data-id="element-1383">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1384">
            <MapPin className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1385" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1386">
              Connectivity
            </span>
            <h2 className="text-4xl font-serif mb-6" data-id="element-1387">
              Transportation & Access
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1388">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1389">
              <Car className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1390" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1391">215 Beltway</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1392">
                The northern curve of the Beltway provides rapid transit to
                Summerlin and the western valley.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1393">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1394" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1395">I-15 Access</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1396">
                Direct access to downtown Las Vegas, the Strip, and the primary
                route to Utah and points north.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1397">
              <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1398" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1399">Nellis AFB</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1400">
                Home to the Thunderbirds, providing a massive economic and
                cultural anchor to the region.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1401">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1402" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1403">Craig Road</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1404">
                A massive east-west commercial corridor offering every daily
                convenience imaginable.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <RelatedCommunities currentCommunity="North Las Vegas" data-id="element-1405" />

      <ListingsComingSoon area="North Las Vegas" data-id="element-1406" />

      <ContactCTA data-id="element-1407" />
    </main>;
}